import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useRunBrandTask } from "../../hooks/useCadence";
import { useBriefDocument } from "../../hooks/useCampaigns";
import { getWorkflowRenderer } from "../../workflows/registry";
import { useWorkflowDefinition } from "../../workflows/hooks/useWorkflowDefinition";
import { useWorkflowStepRuns } from "../../workflows/hooks/useWorkflowStepRuns";
import type { WorkflowContext } from "../../workflows/types";

type WorkflowStepsPanelProps = {
  workflowId: string;
  context: WorkflowContext;
  onError?: (message: string | null) => void;
};

const BRIEF_STATES_LIKELY_WITH_DRAFT = {
  ideation: false,
  researching: false,
  briefed: false,
  drafting: true,
  voice_review: true,
  in_review: true,
  approved: true,
  scheduled: true,
  published: true,
  blocked: true,
  cancelled: false,
} satisfies Record<WorkflowContext["brief"]["state"], boolean>;

function stepRunStorageKey(
  workflowId: string,
  campaignId: string,
  briefId: string,
): string {
  return `workflow-step-runs:${workflowId}:${campaignId}:${briefId}`;
}

function briefStateLikelyHasDraft(state: WorkflowContext["brief"]["state"]): boolean {
  return BRIEF_STATES_LIKELY_WITH_DRAFT[state];
}

export function WorkflowStepsPanel({
  workflowId,
  context,
  onError,
}: WorkflowStepsPanelProps) {
  const definition = useWorkflowDefinition(workflowId, context);
  const runBrandTask = useRunBrandTask(context.brandId);
  const stepRuns = useWorkflowStepRuns({
    storageKey: stepRunStorageKey(workflowId, context.campaignId, context.brief.id),
    steps: definition.resolvedSteps,
  });

  const shouldFetchDraft = useMemo(
    () => definition.resolvedSteps.some((step) => {
      if (!step.readArtifacts?.includes("draft")) return false;
      return (
        Boolean(context.draftText) ||
        briefStateLikelyHasDraft(context.brief.state) ||
        Boolean(stepRuns.getRunId(step.id))
      );
    }),
    [context.brief.state, context.draftText, definition.resolvedSteps, stepRuns],
  );
  const draftDocument = useBriefDocument(
    context.campaignId,
    context.brief.id,
    "draft",
    shouldFetchDraft,
  );

  const runtimeContext = useMemo(
    () => ({
      ...context,
      draftText: context.draftText ?? draftDocument.data?.data?.content ?? null,
    }),
    [context, draftDocument.data?.data?.content],
  );

  async function handleRun(stepId: string) {
    const step = definition.resolvedSteps.find((candidate) => candidate.id === stepId);
    if (!step || !step.taskSlug || !step.buildParams || !step.runButtonLabel) return;
    const eligibility = step.isEligible(runtimeContext);
    if (!eligibility.eligible) {
      onError?.(eligibility.reason ?? "This step is not eligible yet.");
      return;
    }

    onError?.(null);
    stepRuns.startStep(step.id);
    try {
      const result = await runBrandTask.mutateAsync({
        task_slug: step.taskSlug(runtimeContext),
        params: step.buildParams(runtimeContext),
      });
      stepRuns.finishStep(step.id, result.run_id);
    } catch (error) {
      stepRuns.finishStep(step.id);
      onError?.(error instanceof Error ? error.message : `Could not run ${step.label.toLowerCase()}`);
    }
  }

  return (
    <div className="space-y-3">
      {definition.resolvedSteps.map((step) => {
        const Renderer = getWorkflowRenderer(step.renderer);
        if (!Renderer) return null;
        const stepState = stepRuns.getStepState(step);
        const eligibility = step.isEligible(runtimeContext);
        const callToAction = eligibility.eligible
          ? (step.callToAction?.(runtimeContext) ?? null)
          : null;
        const canRunAction = eligibility.eligible && Boolean(step.taskSlug && step.buildParams && step.runButtonLabel);
        const rendererOutput = Renderer({
          step: step as never,
          context: runtimeContext,
          state: stepState as never,
        });

        return (
          <div
            key={step.id}
            className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700"
          >
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-semibold text-slate-900">{step.label}</p>
                {!eligibility.eligible && (
                  <p className="mt-1 text-xs text-slate-500">{eligibility.reason ?? "Not available yet."}</p>
                )}
                {step.notes && step.notes.length > 0 && (
                  <ul className="mt-2 space-y-1 text-xs text-slate-500">
                    {step.notes.map((note, index) => (
                      <li key={`${index}:${note}`}>{note}</li>
                    ))}
                  </ul>
                )}
              </div>
              {canRunAction ? (
                <button
                  type="button"
                  onClick={() => void handleRun(step.id)}
                  disabled={runBrandTask.isPending && stepState.isActive}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {runBrandTask.isPending && stepState.isActive
                    ? (step.runningLabel ?? "Running…")
                    : step.runButtonLabel}
                </button>
              ) : callToAction && eligibility.eligible ? (
                <Link
                  to={callToAction.href}
                  className="inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
                >
                  {callToAction.label}
                </Link>
              ) : null}
            </div>
            {rendererOutput ? (
              <div className="mt-3">
                {rendererOutput}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
